import { Link } from "react-router-dom";
import { Check, ArrowRight, ClipboardList, Users, MapPin, PhoneCall } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { SEO } from "../components/SEO";
import { SITE, IMG } from "../data/siteData";

const coverage = [
  "Successfully introducing a new dog into your family",
  "Fixing specific behaviour issues",
  "Basic manners and obedience",
  "Behaviour modification",
  "Advanced on and off-leash obedience",
  "The freedom of reliable off-leash control",
];

export default function PrivateTraining() {
  return (
    <div data-testid="private-training-page">
      <SEO
        title="Private Dog Training Lessons | Chinook K9 Calgary & Area"
        description="One-on-one private dog training lessons near Calgary, AB — 6-session package $1,200, virtual lessons $200/hour. Customized coaching for obedience, manners and behaviour issues."
        path="/services/private-training"
      />

      <section className="relative min-h-[70vh] flex items-end overflow-hidden" data-testid="pt-hero">
        <img src={IMG.privateWide} alt="Private dog training lesson at Chinook K9" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">Private Training Program</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              One-on-one coaching, built around your goals
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl leading-relaxed hero-text-shadow">
              Whatever your goal, our Private Training Program can put you and your canine on the path to success.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="pt-how">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">How does the Private Training Program work?</h2>
            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>We take your goals and break them down into a series of one-on-one lessons where you will work and learn alongside your dog with instruction from one of our experienced trainers.</p>
              <p>The first session begins with an evaluation to determine the needs of your dog, establish the goals you'd like to accomplish, and map out exactly how we can accomplish them.</p>
              <p>We also offer unlimited support via phone or email during and after your dog's training program to ensure success.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8">
              <h3 className="font-heading text-lg font-bold text-white mb-5">What we can cover</h3>
              <ul className="space-y-3" data-testid="pt-coverage">
                {coverage.map((c) => (
                  <li key={c} className="flex gap-2.5 text-sm text-white/75 leading-relaxed">
                    <Check size={16} className="text-brand shrink-0 mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900" data-testid="pt-details">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Program Details</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Flexible lesson packages</h2>
            <p className="mt-4 text-slate-600"><span className="font-heading font-extrabold text-brand text-xl">$1,200</span> for a 6-session private package — or train from anywhere with virtual lessons at <span className="font-heading font-extrabold text-brand text-xl">$200/hour</span>.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: ClipboardList, title: "Initial Evaluation", desc: "Your first session begins with a full evaluation of your dog's needs and your goals, so every lesson has a clear purpose." },
              { icon: Users, title: "Learn Alongside Your Dog", desc: "You work hands-on with your dog in every lesson, building the skills and confidence to maintain training for life." },
              { icon: MapPin, title: "At Our Training Facility", desc: "All in-person sessions take place at the Chinook K9 facility just outside Calgary, AB. Off-site and in-home sessions are available case-by-case based on goals (travel fees apply)." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="border border-black/10 rounded-2xl p-8 h-full hover:border-brand/50 hover:shadow-xl transition-[border-color,box-shadow]" data-testid={`pt-detail-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-5"><c.icon size={22} /></div>
                  <h3 className="font-heading text-lg font-bold mb-3">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900 border-t border-black/10" id="virtual" data-testid="virtual-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-16">
            <Reveal>
              <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">New · Train From Anywhere</p>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6">Virtual Lessons</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Live, one-on-one video coaching with a certified Chinook K9 trainer — same customized plan,
                same expert guidance, from the comfort of your home. Perfect for puppy guidance, manners,
                behaviour consults, and follow-up coaching after a program.
              </p>
              <ul className="space-y-2.5 mb-7">
                {["Live one-hour video sessions", "Customized homework between sessions", "Ideal for follow-ups and tune-ups", "Available anywhere — no travel needed"].map((v) => (
                  <li key={v} className="flex gap-2.5 text-sm text-slate-600"><Check size={16} className="text-brand shrink-0 mt-0.5" /> {v}</li>
                ))}
              </ul>
              <p className="inline-block bg-obsidian text-white font-heading font-extrabold text-2xl px-7 py-3 rounded-full" data-testid="virtual-price">
                $200 <span className="text-brand text-base font-bold">/ hour session</span>
              </p>
              <Link to="/virtual-dog-training" data-testid="pt-virtual-page-link" className="mt-6 inline-flex items-center gap-2 text-brand font-heading font-bold text-sm hover:gap-3 transition-all">
                Visit the Virtual Lessons page <ArrowRight size={15} />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-3 border-2 border-brand/30 rounded-2xl translate-x-4 translate-y-4" />
                <img src={IMG.virtualLessons} alt="Happy dog at a Chinook K9 virtual training session" className="relative rounded-2xl w-full h-auto" data-testid="virtual-image" />
              </div>
            </Reveal>
          </div>
          <Reveal className="max-w-3xl mx-auto">
            <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-center">Book a virtual session</h3>
            <p className="text-slate-600 text-center mb-8">Tell us about your dog — we'll set up your first video session.</p>
            <div className="bg-obsidian rounded-2xl p-6 sm:p-10">
              <LeadForm compact defaultPrograms={["Virtual Lessons"]} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20 border-t border-white/10" data-testid="pt-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Ready to get started?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Tell us about your dog and your goals — we'll recommend the right path forward.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="pt-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Contact Us <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} data-testid="pt-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <PhoneCall size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
